import React from 'react';
import { addDays, addWeeks, format, isValid, parseISO, startOfWeek } from 'date-fns';
import Card from './Card';
import Button from './Button';
import { ChevronLeft, ChevronRight, CalendarDays } from 'lucide-react';

/** The Monday of the week containing `date`, as `yyyy-MM-dd`. */
export const toWeekStart = (date: Date): string =>
  format(startOfWeek(date, { weekStartsOn: 1 }), 'yyyy-MM-dd');

interface WeekSelectorProps {
  weekStartDate: string;
  onWeekChange: (newStartDate: string) => void;
  minDate?: string;
  maxDate?: string;
  className?: string;
}

const WeekSelector: React.FC<WeekSelectorProps> = ({
  weekStartDate,
  onWeekChange,
  minDate,
  maxDate,
  className = ''
}) => {
  // Dates are handled as local days throughout: `new Date('2026-09-14')` is UTC midnight, so a
  // round-trip through toISOString() lands on the previous day west of Greenwich.
  const current = parseISO(weekStartDate);

  const handlePreviousWeek = () => onWeekChange(format(addWeeks(current, -1), 'yyyy-MM-dd'));

  const handleNextWeek = () => onWeekChange(format(addWeeks(current, 1), 'yyyy-MM-dd'));

  const handlePickDate = (value: string) => {
    const picked = parseISO(value);
    if (isValid(picked)) onWeekChange(toWeekStart(picked));
  };

  const formatWeekRange = (start: Date): string => {
    const end = addDays(start, 4); // Monday to Friday
    return `${format(start, 'MMM d, yyyy')} - ${format(end, 'MMM d, yyyy')}`;
  };

  return (
    <Card className={className}>
      <Card.Body>
        <div className="flex items-center justify-between gap-4">
          <Button
            variant="outline"
            onClick={handlePreviousWeek}
            leftIcon={ChevronLeft}
            disabled={minDate ? weekStartDate <= minDate : false}
          >
            Previous Week
          </Button>

          <div className="text-center">
            <div className="text-lg font-semibold text-gray-800 dark:text-gray-200">
              {formatWeekRange(current)}
            </div>
            <div className="text-sm text-gray-500 dark:text-gray-400">
              Week of {format(current, 'MMMM d')}
            </div>
          </div>

          <Button
            variant="outline"
            onClick={handleNextWeek}
            rightIcon={ChevronRight}
            disabled={maxDate ? weekStartDate >= maxDate : false}
          >
            Next Week
          </Button>
        </div>

        <div className="flex items-center justify-center gap-3 mt-4">
          <label htmlFor="week-jump" className="sr-only">Jump to week</label>
          <input
            id="week-jump"
            type="date"
            value={weekStartDate}
            onChange={(e) => handlePickDate(e.target.value)}
            min={minDate}
            max={maxDate}
            className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          />
          <Button
            variant="outline"
            size="sm"
            leftIcon={CalendarDays}
            onClick={() => onWeekChange(toWeekStart(new Date()))}
          >
            This week
          </Button>
        </div>
      </Card.Body>
    </Card>
  );
};

export default WeekSelector;
